import type { GameConfig } from "../config/GameConfig";
import { clamp } from "../core/math";

/**
 * Lá menor pentatónico (A C D E G): sem semitom, quintas abertas.
 * Registo grave = mar; agudo = ar. A linha vive na quinta A–E.
 * O bed activo vem de `Level.audio` (`synth-am` hoje). Ainda não está fechado.
 */
const A2 = 110;
const C2 = 65.41;
const D2 = 73.42;
const E2 = 82.41;
const G2 = 98;
const C3 = 130.81;
const D3 = 146.83;
const E3 = 164.81;
const G3 = 196;
const A3 = 220;
const C4 = 261.63;
const D4 = 293.66;
const E4 = 329.63;
const G4 = 392;
const A4 = 440;
const C5 = 523.25;
const E5 = 659.26;
const G5 = 783.99;
const A5 = 880;
const C6 = 1046.5;

type Chord = {
  bass: number;
  fifth: number;
  mid: number;
  high: number;
  high2: number;
  color: number;
};

/** Am → C → G → Em7, todos dentro de A C D E G (sem semitom). */
const CHORDS: Chord[] = [
  { bass: A2, fifth: E3, mid: A3, high: E4, high2: A4, color: C5 },
  { bass: C2, fifth: G2, mid: C3, high: G3, high2: E4, color: G4 },
  { bass: G2, fifth: D3, mid: G3, high: D4, high2: G4, color: A4 },
  { bass: E2, fifth: G2, mid: E3, high: G3, high2: A4, color: C5 },
];

const LOWER_CHIMES = [A2, C3, D3, E3, G3, A3];
const UPPER_CHIMES = [E4, G4, A4, C5, E5];
const PENT = [C2, D2, E2, G2, A2, C3, D3, E3, G3, A3, C4, D4, E4, G4, A4, C5, E5, G5, A5];
const HARP = [E4, G4, A4, C5, E5];
const STRING = [G4, A4, C5, E5, G5, A5, C6];

const Fs2 = 92.5;
const Fs3 = 185;
const Fs4 = 369.99;
const B2 = 123.47;
const B3 = 246.94;
const Cs3 = 138.59;
const Cs4 = 277.18;
const Cs5 = 554.37;
const E4b = 329.63;

const PASS_CHORDS: Chord[] = [
  { bass: Fs2, fifth: Cs3, mid: Fs3, high: A3, high2: Cs4, color: E4b },
  { bass: A2, fifth: E3, mid: A3, high: Cs4, high2: E4b, color: Fs4 },
  { bass: B2, fifth: Fs3, mid: B3, high: E4b, high2: Fs4, color: A4 },
  { bass: E2, fifth: B2, mid: E3, high: A3, high2: Cs4, color: Fs4 },
];
const PASS_LOWER = [Fs2, A2, B2, Cs3, E3, Fs3];
const PASS_UPPER = [Cs4, E4b, Fs4, A4, Cs5];
const PASS_PENT = [Fs2, A2, B2, Cs3, E3, Fs3, A3, B3, Cs4, E4b, Fs4, A4, Cs5, E5, A5];
const PASS_HARP = [Cs4, E4b, Fs4, A4, Cs5];
const PASS_STRING = [E4b, Fs4, A4, Cs5, E5, A5];

type BedTables = {
  chords: Chord[];
  lower: number[];
  upper: number[];
  pent: number[];
  harp: number[];
  string: number[];
};

const AM_BED: BedTables = {
  chords: CHORDS,
  lower: LOWER_CHIMES,
  upper: UPPER_CHIMES,
  pent: PENT,
  harp: HARP,
  string: STRING,
};

const PASS_BED: BedTables = {
  chords: PASS_CHORDS,
  lower: PASS_LOWER,
  upper: PASS_UPPER,
  pent: PASS_PENT,
  harp: PASS_HARP,
  string: PASS_STRING,
};

/** 4 vozes soam; 2 esperam. A cada ~30s entra uma e sai outra. */
const VOICE_ON = [1, 1, 0.9, 0.75, 0, 0];

export interface AudioFrame {
  inflate: number;
  wiggle: number;
  pulse: number;
  wiggleRate: number;
  pulseRate: number;
  lineHeat: number;
  upperMass: number;
  lowerMass: number;
  upperCount: number;
  lowerCount: number;
  avgUpper: number;
  avgLower: number;
  growing: number;
  meeting: number;
  tension: number;
  flow: number;
  gust: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private ready = false;
  private live = 0;
  private currentEnergy = 0;
  private chimeWait = 12;
  private fromChord = 0;
  private toChord = 0;
  private chordBlend = 1;
  private voiceWait = 22 + Math.random() * 16;
  private readonly voiceMix = VOICE_ON.slice();
  private readonly voiceTarget = VOICE_ON.slice();
  private readonly voiceAlt = [0, 0, 0, 1, 1, 2];
  private readonly bowls: PartialPair[] = [];
  private harp: PartialPair | null = null;
  private gongFund: PartialPair | null = null;
  private gongFifth: PartialPair | null = null;
  private gongOct: PartialPair | null = null;
  private gongHold = 0;
  private gongMix = 0;
  private gongSize = 0;
  private gongAbove = true;
  private wind: NoisePad | null = null;
  private water: NoisePad | null = null;
  private master!: GainNode;
  private bedGain!: GainNode;
  private fxGain!: GainNode;
  private reverbSend!: GainNode;
  private currentGain!: GainNode;
  private currentFilter!: BiquadFilterNode;
  private noiseBuf: AudioBuffer | null = null;
  private clingAt = 0;
  private gongAt = 0;
  private wakeRise = 0;
  private wakeSilence = 0;
  private remMix = 0;
  private rem: RemPad | null = null;

  constructor(private readonly config: GameConfig) {
    document.addEventListener("visibilitychange", () => {
      if (!this.ctx || !this.ready) return;
      if (document.hidden) void this.ctx.suspend();
      else if (this.config.audio.enabled) void this.ctx.resume();
    });
  }

  private pal(): BedTables {
    return this.config.audio.bedId === "synth-pass" ? PASS_BED : AM_BED;
  }

  get unlocked(): boolean {
    return this.ready;
  }

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return;
    }

    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    this.ctx = ctx;
    void ctx.resume();
    try {
      this.build(ctx);
      this.prime(ctx);
      this.ready = true;
    } catch (err) {
      console.error("Onirya audio failed", err);
      this.ctx = null;
    }
  }

  noticeCurrent(intensity: number): void {
    this.currentEnergy = Math.max(this.currentEnergy, clamp(intensity, 0, 1));
  }

  absorb(radiusNorm: number, above: boolean): void {
    const ctx = this.liveCtx();
    if (!ctx) return;
    const n = clamp(radiusNorm, 0.12, 1);
    const pal = this.pal();
    const scale = above ? pal.upper : pal.lower;
    const freq = scale[Math.min(scale.length - 1, Math.floor((1 - n) * (scale.length - 1)))];
    this.tone(ctx, freq, 1.6 + n * 1.4, 0.035 + n * 0.03, 0, freq * 0.5);
    this.noiseBurst(ctx, {
      duration: 0.55 + n * 0.4,
      band: above ? 900 : 280,
      q: 1.8,
      gain: 0.035 + n * 0.04,
      sweep: 0.55,
    });
  }

  merge(radiusNorm: number, above: boolean, otherNorm = 0.4): void {
    this.advanceChord();
    const ctx = this.liveCtx();
    if (!ctx) return;
    const n = clamp(radiusNorm, 0.12, 1);
    const o = clamp(otherNorm, 0.12, 1);
    const pent = this.pal().pent;
    const chord = this.pal().chords[this.toChord];
    const pool = uniqueSorted([
      nearestPent(chord.bass, pent),
      chord.fifth,
      chord.mid,
      chord.high,
      chord.high2,
      chord.color,
      nearestPent(chord.color * 2, pent),
    ]);
    const idxA = clamp(Math.round((1 - o) * (pool.length - 1) + (above ? 0.6 : 0)), 0, pool.length - 1);
    const idxB = clamp(Math.round((1 - n) * (pool.length - 1) + (above ? 0.6 : 0)), 0, pool.length - 1);
    let iLow = Math.min(idxA, idxB);
    let iHigh = Math.max(idxA, idxB);
    if (iHigh === iLow) iHigh = Math.min(pool.length - 1, iLow + 1);
    if (iHigh === iLow && iLow > 0) iLow -= 1;
    const a = pool[iHigh];
    const b = pool[iLow];
    const home = pool[idxB];
    const g = 0.08 + n * 0.04;
    this.pluck(ctx, a, 0.4, 0.07);
    this.pluck(ctx, b, 0.38, 0.06);
    this.tone(ctx, a, 2.4, g, 0, home);
    this.tone(ctx, b, 2.6, g * 0.9, 0.05, home);
    this.tone(ctx, nearestPent(home * 1.5, pent), 2.8, g * 0.38, 0.12);
    this.tone(ctx, nearestPent(home * 2, pent), 2.0, g * 0.18, 0.18);
    this.noiseBurst(ctx, {
      duration: 0.85 + n * 0.4,
      band: above ? 720 : 240,
      q: 1.4,
      gain: 0.04 + n * 0.03,
      sweep: 0.45,
    });
  }

  meet(points: number): void {
    this.advanceChord();
    const ctx = this.liveCtx();
    if (!ctx) return;
    const n = clamp(points / 40, 0.2, 1);
    const chord = this.pal().chords[this.toChord];
    this.tone(ctx, chord.mid, 2.6, 0.04 * n);
    this.tone(ctx, chord.high, 3.0, 0.035 * n, 0.1);
    this.tone(ctx, chord.color, 3.4, 0.022 * n, 0.18);
  }

  /** Sino de taça: cada bola nova. Lado = registo; X = grau da pentatónica. */
  bell(above: boolean, xNorm: number): void {
    const ctx = this.liveCtx();
    if (!ctx) return;
    const pal = this.pal();
    const scale = above ? pal.upper : pal.lower;
    const idx = clamp(Math.round(xNorm * (scale.length - 1) + (Math.random() - 0.5) * 0.8), 0, scale.length - 1);
    const freq = scale[idx];
    const dur = above ? 4.2 : 5.2;
    const g = above ? 0.038 : 0.046;
    this.tone(ctx, freq, dur, g);
    this.tone(ctx, freq * 1.5, dur * 0.82, g * 0.38, 0.04);
    this.tone(ctx, freq * 2, dur * 0.62, g * 0.22, 0.08);
    this.noiseBurst(ctx, {
      duration: 0.28,
      band: above ? 2400 : 620,
      q: 4.2,
      gain: 0.016,
      sweep: 0.35,
    });
  }

  /** Corda curta: o rato a passar ou varrer a linha. */
  cling(xNorm: number, speed = 0.4): void {
    const ctx = this.liveCtx();
    if (!ctx) return;
    const t = ctx.currentTime;
    if (t - this.clingAt < 0.07) return;
    this.clingAt = t;
    const strings = this.pal().string;
    const idx = clamp(Math.round(xNorm * (strings.length - 1)), 0, strings.length - 1);
    const freq = strings[idx];
    const g = 0.07 + clamp(speed, 0, 1) * 0.045;
    this.pluck(ctx, freq, 0.32, g);
    this.tone(ctx, freq * 2, 0.16, g * 0.22, 0);
    this.noiseBurst(ctx, {
      duration: 0.07,
      band: 3200 + speed * 1400,
      q: 6.5,
      gain: 0.028 + speed * 0.02,
      sweep: 0.55,
    });
  }

  /** Gong: passar o rato sobre uma bola. Grande = grave e alto; pequena = agudo e baixo. */
  gong(sizeNorm: number, above: boolean): void {
    this.gongFollow(sizeNorm, above);
    const ctx = this.liveCtx();
    if (!ctx) return;
    const t = ctx.currentTime;
    if (t - this.gongAt < 0.18) return;
    this.gongAt = t;
    const voice = this.gongVoice(this.gongSize, above);
    this.pluck(ctx, voice.freq, 0.45, 0.05 + this.gongSize * 0.04);
    this.noiseBurst(ctx, {
      duration: 0.55 + this.gongSize * 0.5,
      band: 160 + (1 - this.gongSize) * 1400,
      q: 1.15,
      gain: 0.028 + this.gongSize * 0.03,
      sweep: 0.38,
    });
  }

  gongFollow(sizeNorm: number, above: boolean): void {
    this.gongSize = clamp(sizeNorm, 0, 1);
    this.gongAbove = above;
    this.gongHold = 1;
  }

  gongRelease(): void {
    this.gongHold = 0;
  }

  setWin(gameOut: number, rem: number): void {
    this.wakeRise = 0;
    this.wakeSilence = clamp(gameOut, 0, 1);
    this.remMix = clamp(rem, 0, 1);
  }

  resetWin(): void {
    this.wakeRise = 0;
    this.wakeSilence = 0;
    this.remMix = 0;
    this.fromChord = 0;
    this.toChord = 0;
    this.chordBlend = 1;
  }

  private advanceChord(): void {
    this.fromChord = this.toChord;
    this.toChord = (this.toChord + 1) % this.pal().chords.length;
    this.chordBlend = 0;
  }

  update(dt: number, frame: AudioFrame): void {
    this.currentEnergy *= Math.exp(-dt * 2.4);
    if (!this.ready || !this.ctx) return;
    if (this.ctx.state === "suspended" && !document.hidden) void this.ctx.resume();
    const cfg = this.config.audio;
    if (!cfg.enabled) {
      this.master.gain.value = 0;
      return;
    }

    this.live += dt;
    const now = this.ctx.currentTime;
    this.chordBlend = Math.min(1, this.chordBlend + dt / 5.5);
    this.tickVoices(dt);
    const rise = this.wakeRise;
    const dry = 1 - this.wakeSilence;
    const pitch = 1 + rise * 1.25;
    this.master.gain.value = cfg.master;
    this.bedGain.gain.value = cfg.bed * dry;
    this.fxGain.gain.value = cfg.fx * dry;
    this.reverbSend.gain.value = (0.55 + cfg.reverb * 0.7) * (1 + this.remMix * 0.55);
    this.rem?.set(this.remMix, now);

    const inflate = clamp(frame.inflate, 0, 1);
    const wiggle = clamp(frame.wiggle, 0, 1);
    const pulse = clamp(frame.pulse, 0, 1);
    const meet = clamp(frame.meeting, 0, 1);
    const whoosh = clamp(this.currentEnergy, 0, 1);
    const flow = clamp(frame.flow, 0, 1);
    const gust = clamp(frame.gust, 0, 1);
    const breeze = clamp(whoosh * 0.55 + gust * 0.7 + flow * 0.45, 0, 1);
    const breath = 0.5 + 0.5 * Math.sin(this.live * (0.04 + frame.wiggleRate * 0.05));
    const swell = 0.5 + 0.5 * Math.sin(this.live * (0.07 + frame.pulseRate * 0.08) + 1.2);
    const detune = 2.2 + wiggle * 7 + inflate * 3 + breeze * 5;

    const excite = clamp(
      inflate * 0.42 +
        wiggle * 0.18 +
        pulse * 0.12 +
        frame.lineHeat * 0.18 +
        frame.wiggleRate * 0.05 +
        frame.pulseRate * 0.05,
      0,
      1,
    );
    const lineGain = 0.5 + excite * 0.85;

    const lowerAmt =
      (0.16 + frame.lowerMass * 0.22 + inflate * 0.12 + frame.lineHeat * 0.08) * lineGain;
    const upperAmt =
      (0.12 + frame.upperMass * 0.2 + inflate * 0.08 + frame.growing * 0.05) * lineGain;
    const fifthAmt = (0.08 + inflate * 0.18 + frame.lineHeat * 0.12 + pulse * 0.06) * lineGain;
    const colorAmt = (meet * 0.07 + inflate * 0.03) * lineGain;

    const trem = 1 - pulse * 0.12 * (0.5 + 0.5 * swell);
    const from = this.pal().chords[this.fromChord];
    const to = this.pal().chords[this.toChord];
    const t = this.chordBlend;
    const v = this.voiceMix;
    const alt = this.voiceAlt;

    const pal = this.pal();
    const pent = pal.pent;
    this.bowls[0]?.set(
      slotHz(from, to, t, 0, alt[0], pent) * pitch,
      detune,
      lowerAmt * trem * (0.94 + breath * 0.06) * v[0] * dry,
      now,
    );
    this.bowls[1]?.set(
      slotHz(from, to, t, 1, alt[1], pent) * pitch,
      detune * 0.8,
      (lowerAmt * 0.7 + fifthAmt) * trem * v[1] * dry,
      now,
    );
    this.bowls[2]?.set(
      slotHz(from, to, t, 2, alt[2], pent) * pitch,
      detune,
      (0.07 * lineGain + inflate * 0.14 * lineGain + fifthAmt * 0.5) * trem * v[2] * dry,
      now,
    );
    this.bowls[3]?.set(
      slotHz(from, to, t, 3, alt[3], pent) * pitch,
      detune * 1.1,
      upperAmt * (0.94 + breath * 0.06) * v[3] * dry,
      now,
    );
    this.bowls[4]?.set(
      slotHz(from, to, t, 4, alt[4], pent) * pitch,
      detune,
      (upperAmt * 0.55 + inflate * 0.05 * lineGain) * v[4] * dry,
      now,
    );
    this.bowls[5]?.set(
      slotHz(from, to, t, 5, alt[5], pent) * pitch,
      detune * 1.2,
      (colorAmt + frame.upperMass * 0.04 * lineGain) * v[5] * dry,
      now,
    );

    const windGain =
      (0.018 + frame.upperMass * 0.05 + wiggle * 0.04 + whoosh * 0.07 + frame.growing * 0.02 + breeze * 0.04) *
      (0.85 + breath * 0.15);
    const waterGain =
      0.022 + frame.lowerMass * 0.06 + inflate * 0.03 + frame.lineHeat * 0.03 + flow * 0.018;
    const windHz = 480 + wiggle * 520 + whoosh * 700 + breath * 180 + breeze * 640;
    const waterHz = 180 + inflate * 120 + frame.lowerMass * 80 + flow * 70;

    this.wind?.set(windHz * pitch, 1.1 + wiggle * 0.4 + breeze * 0.35, windGain * dry, now);
    this.water?.set(waterHz * pitch, 0.85 + flow * 0.15, waterGain * dry, now);

    this.currentGain.gain.value = (whoosh * 0.045 + gust * 0.03) * cfg.fx * dry;
    this.currentFilter.frequency.value = 520 + whoosh * 900 + breeze * 480;

    const harp = this.pal().harp;
    const harpPos = breeze * (harp.length - 1);
    const harpI = Math.floor(harpPos);
    const harpN = Math.min(harp.length - 1, harpI + 1);
    const harpHz = harp[harpI] + (harp[harpN] - harp[harpI]) * (harpPos - harpI);
    this.harp?.set(harpHz * pitch, 4 + breeze * 8, breeze * 0.032 * (0.7 + breath * 0.3) * dry, now);

    this.tickGong(dt, now, pitch, dry);
    if (rise <= 0) this.tickChimes(dt, frame, now);
  }

  private tickVoices(dt: number): void {
    this.voiceWait -= dt;
    if (this.voiceWait <= 0) this.rotateVoice();
    const k = 1 - Math.exp(-dt / 7.5);
    for (let i = 0; i < this.voiceMix.length; i++) {
      this.voiceMix[i] += (this.voiceTarget[i] - this.voiceMix[i]) * k;
    }
  }

  private rotateVoice(): void {
    this.voiceWait = 30 + (Math.random() * 2 - 1) * 10;
    const on: number[] = [];
    const off: number[] = [];
    for (let i = 0; i < this.voiceTarget.length; i++) {
      if (this.voiceTarget[i] > 0.35) on.push(i);
      else off.push(i);
    }
    if (off.length === 0) {
      const drop = 1 + Math.floor(Math.random() * (this.voiceTarget.length - 1));
      this.voiceTarget[drop] = 0;
      this.voiceAlt[drop] = (this.voiceAlt[drop] + 1 + Math.floor(Math.random() * 2)) % 3;
      return;
    }
    const dropPool = on.filter((i) => i !== 0);
    const drop = dropPool[Math.floor(Math.random() * dropPool.length)] ?? on[0];
    const enter = off[Math.floor(Math.random() * off.length)];
    this.voiceTarget[drop] = 0;
    this.voiceTarget[enter] = on.length > 4 ? 0.7 : 1;
    this.voiceAlt[enter] = (this.voiceAlt[enter] + 1 + Math.floor(Math.random() * 2)) % 3;
  }

  private tickGong(dt: number, now: number, pitch = 1, dry = 1): void {
    const k = 1 - Math.exp(-dt / (this.gongHold > 0.5 ? 0.12 : 0.45));
    this.gongMix += (this.gongHold - this.gongMix) * k;
    const voice = this.gongVoice(this.gongSize, this.gongAbove);
    const mix = this.gongMix * dry;
    const detune = 3 + this.gongSize * 4;
    const pent = this.pal().pent;
    this.gongFund?.set(voice.freq * pitch, detune, voice.gain * mix, now, 0.22, 0.12);
    this.gongFifth?.set(nearestPent(voice.freq * 1.5, pent) * pitch, detune * 0.8, voice.gain * 0.42 * mix, now, 0.22, 0.12);
    this.gongOct?.set(nearestPent(voice.freq * 2, pent) * pitch, detune * 1.1, voice.gain * 0.2 * mix, now, 0.22, 0.12);
  }

  private gongVoice(size: number, above: boolean): { freq: number; gain: number } {
    const n = clamp(size, 0, 1);
    const pent = this.pal().pent;
    const pos = clamp((1 - n) * (pent.length - 1) + (above ? 0.7 : 0), 0, pent.length - 1);
    const i = Math.floor(pos);
    const t = pos - i;
    const a = pent[i];
    const b = pent[Math.min(pent.length - 1, i + 1)];
    return {
      freq: a + (b - a) * t,
      gain: 0.016 + n * n * 0.09,
    };
  }

  private tickChimes(dt: number, frame: AudioFrame, _now: number): void {
    const count = frame.upperCount + frame.lowerCount;
    if (count <= 0) {
      this.chimeWait = Math.max(this.chimeWait, 5);
      return;
    }
    this.chimeWait -= dt;
    if (this.chimeWait > 0) return;

    const density = clamp(count / 8, 0, 1);
    this.chimeWait = 14 + Math.random() * 12 - density * 4;

    const preferUpper = Math.random() < 0.5 + (frame.upperMass - frame.lowerMass) * 0.25;
    const hasUpper = frame.upperCount > 0;
    const hasLower = frame.lowerCount > 0;
    const upper = hasUpper && (!hasLower || preferUpper);
    const pal = this.pal();
    const scale = upper ? pal.upper : pal.lower;
    const size = upper ? frame.avgUpper : frame.avgLower;
    const idx = clamp(
      Math.round((1 - size) * (scale.length - 1) + (Math.random() - 0.5)),
      0,
      scale.length - 1,
    );
    const ctx = this.ctx;
    if (!ctx) return;
    const dur = 2.2 + size * 2.2;
    this.tone(ctx, scale[idx], dur, 0.018 + size * 0.02);
  }

  private liveCtx(): AudioContext | null {
    if (!this.ready || !this.ctx || !this.config.audio.enabled) return null;
    if (this.wakeRise > 0 || this.wakeSilence > 0.12 || this.remMix > 0.15) return null;
    return this.ctx;
  }

  private build(ctx: AudioContext): void {
    this.master = ctx.createGain();
    this.master.gain.value = this.config.audio.master;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -18;
    limiter.knee.value = 12;
    limiter.ratio.value = 8;
    limiter.attack.value = 0.01;
    limiter.release.value = 0.28;
    this.master.connect(limiter);
    limiter.connect(ctx.destination);

    this.bedGain = ctx.createGain();
    this.fxGain = ctx.createGain();
    this.reverbSend = ctx.createGain();
    this.bedGain.gain.value = this.config.audio.bed;
    this.fxGain.gain.value = this.config.audio.fx;
    this.reverbSend.gain.value = this.config.audio.reverb;
    this.bedGain.connect(this.master);
    this.fxGain.connect(this.master);

    try {
      const convolver = ctx.createConvolver();
      convolver.buffer = makeImpulse(ctx, 2.4);
      const verbOut = ctx.createGain();
      verbOut.gain.value = 1;
      this.reverbSend.connect(convolver);
      convolver.connect(verbOut);
      verbOut.connect(this.master);
    } catch {
      this.reverbSend.connect(this.master);
    }

    this.bowls.push(
      new PartialPair(ctx, "sine", this.bedGain, this.reverbSend, true),
      new PartialPair(ctx, "sine", this.bedGain, this.reverbSend),
      new PartialPair(ctx, "sine", this.bedGain, this.reverbSend),
      new PartialPair(ctx, "triangle", this.bedGain, this.reverbSend),
      new PartialPair(ctx, "sine", this.bedGain, this.reverbSend),
      new PartialPair(ctx, "sine", this.bedGain, this.reverbSend),
    );
    this.harp = new PartialPair(ctx, "sine", this.bedGain, this.reverbSend);
    this.gongFund = new PartialPair(ctx, "sine", this.fxGain, this.reverbSend, true);
    this.gongFifth = new PartialPair(ctx, "sine", this.fxGain, this.reverbSend);
    this.gongOct = new PartialPair(ctx, "triangle", this.fxGain, this.reverbSend);

    this.noiseBuf = makeNoise(ctx, 3);
    this.wind = new NoisePad(ctx, this.noiseBuf, "bandpass", this.bedGain, this.reverbSend);
    this.water = new NoisePad(ctx, this.noiseBuf, "lowpass", this.bedGain, this.reverbSend);
    this.rem = new RemPad(ctx, this.master);

    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuf;
    noise.loop = true;
    this.currentFilter = ctx.createBiquadFilter();
    this.currentFilter.type = "bandpass";
    this.currentFilter.Q.value = 1.15;
    this.currentGain = ctx.createGain();
    this.currentGain.gain.value = 0;
    noise.connect(this.currentFilter);
    this.currentFilter.connect(this.currentGain);
    this.currentGain.connect(this.fxGain);
    this.currentGain.connect(this.reverbSend);
    noise.start();
  }

  private prime(ctx: AudioContext): void {
    const silent = ctx.createBuffer(1, 1, ctx.sampleRate);
    const click = ctx.createBufferSource();
    click.buffer = silent;
    click.connect(ctx.destination);
    click.start(0);
    this.tone(ctx, A4, 0.9, 0.05);
    this.tone(ctx, E4, 1.2, 0.03, 0.04);
  }

  private noiseBurst(
    ctx: AudioContext,
    opts: { duration: number; band: number; q: number; gain: number; sweep: number },
  ): void {
    if (!this.noiseBuf) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.Q.value = opts.q;
    const gain = ctx.createGain();
    const t = ctx.currentTime;
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.fxGain);
    gain.connect(this.reverbSend);
    filter.frequency.setValueAtTime(opts.band, t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(80, opts.band * opts.sweep), t + opts.duration);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.001, opts.gain), t + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + opts.duration);
    src.start(t);
    src.stop(t + opts.duration + 0.05);
    src.onended = () => {
      src.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
  }

  /** Pulso de corda: ruído à frequência da nota + triângulo curto. */
  private pluck(ctx: AudioContext, freq: number, duration: number, gainAmt: number): void {
    const t = ctx.currentTime;
    const period = Math.max(24, Math.floor(ctx.sampleRate / Math.max(80, freq)));
    const buf = ctx.createBuffer(1, period, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < period; i++) data[i] = Math.random() * 2 - 1;

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(Math.min(4200, freq * 8), t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(180, freq * 1.6), t + duration);
    filter.Q.value = 1.4;
    const gain = ctx.createGain();
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.fxGain);
    gain.connect(this.reverbSend);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.001, gainAmt), t + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    src.start(t);
    src.stop(t + duration + 0.04);
    src.onended = () => {
      src.disconnect();
      filter.disconnect();
      gain.disconnect();
    };

    const osc = ctx.createOscillator();
    osc.type = "triangle";
    const oscGain = ctx.createGain();
    osc.connect(oscGain);
    oscGain.connect(this.fxGain);
    osc.frequency.setValueAtTime(freq, t);
    oscGain.gain.setValueAtTime(0.0001, t);
    oscGain.gain.exponentialRampToValueAtTime(Math.max(0.001, gainAmt * 0.7), t + 0.004);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, t + duration * 0.85);
    osc.start(t);
    osc.stop(t + duration + 0.04);
    osc.onended = () => {
      osc.disconnect();
      oscGain.disconnect();
    };
  }

  private tone(
    ctx: AudioContext,
    freq: number,
    duration: number,
    gainAmt: number,
    delay = 0,
    endFreq?: number,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = Math.min(2400, freq * 5);
    const gain = ctx.createGain();
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.fxGain);
    gain.connect(this.reverbSend);
    const t = ctx.currentTime + delay;
    osc.frequency.setValueAtTime(freq, t);
    if (endFreq) osc.frequency.exponentialRampToValueAtTime(Math.max(40, endFreq), t + duration);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.001, gainAmt), t + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    osc.start(t);
    osc.stop(t + duration + 0.05);
    osc.onended = () => {
      osc.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
  }
}

class PartialPair {
  private readonly oscA: OscillatorNode;
  private readonly oscB: OscillatorNode;
  private readonly sub: OscillatorNode | null;
  private readonly gain: GainNode;

  constructor(
    ctx: AudioContext,
    type: OscillatorType,
    bed: GainNode,
    verb: GainNode,
    withSub = false,
  ) {
    this.oscA = ctx.createOscillator();
    this.oscB = ctx.createOscillator();
    this.oscA.type = type;
    this.oscB.type = "sine";
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.oscA.connect(this.gain);
    this.oscB.connect(this.gain);
    this.sub = withSub ? ctx.createOscillator() : null;
    if (this.sub) {
      this.sub.type = "sine";
      const subGain = ctx.createGain();
      subGain.gain.value = 0.28;
      this.sub.connect(subGain);
      subGain.connect(this.gain);
      this.sub.start();
    }
    this.gain.connect(bed);
    this.gain.connect(verb);
    this.oscA.start();
    this.oscB.start();
  }

  set(freq: number, detune: number, gain: number, now: number, tau = 1.4, gainTau = 0.4): void {
    this.oscA.frequency.setTargetAtTime(freq, now, tau);
    this.oscB.frequency.setTargetAtTime(freq * 1.0015, now, tau);
    this.oscA.detune.setTargetAtTime(-detune, now, 0.5);
    this.oscB.detune.setTargetAtTime(detune, now, 0.5);
    this.gain.gain.setTargetAtTime(Math.max(0, gain), now, gainTau);
    this.sub?.frequency.setTargetAtTime(freq * 0.5, now, tau);
  }
}

class NoisePad {
  private readonly filter: BiquadFilterNode;
  private readonly gain: GainNode;

  constructor(
    ctx: AudioContext,
    buf: AudioBuffer,
    type: BiquadFilterType,
    bed: GainNode,
    verb: GainNode,
  ) {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    this.filter = ctx.createBiquadFilter();
    this.filter.type = type;
    this.filter.frequency.value = type === "lowpass" ? 200 : 600;
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    src.connect(this.filter);
    this.filter.connect(this.gain);
    this.gain.connect(bed);
    this.gain.connect(verb);
    src.start();
  }

  set(hz: number, q: number, gain: number, now: number): void {
    this.filter.frequency.setTargetAtTime(hz, now, 0.4);
    this.filter.Q.setTargetAtTime(q, now, 0.4);
    this.gain.gain.setTargetAtTime(Math.max(0, gain), now, 0.4);
  }
}

/** Drone lento: sono REM / ponto alto da meditação. */
class RemPad {
  private readonly out: GainNode;

  constructor(ctx: AudioContext, dest: GainNode) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;

    const verb = ctx.createConvolver();
    verb.buffer = makeImpulse(ctx, 5.4);
    const verbMix = ctx.createGain();
    verbMix.gain.value = 0.9;
    this.out.connect(dest);
    this.out.connect(verb);
    verb.connect(verbMix);
    verbMix.connect(dest);

    const drones = [54.5, 81.4, 108.7, 110.2, 163.0, 217.5, 256.0];
    const levels = [0.26, 0.1, 0.15, 0.1, 0.07, 0.04, 0.032];
    for (let i = 0; i < drones.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = drones[i];
      const g = ctx.createGain();
      g.gain.value = levels[i];
      osc.connect(g);
      g.connect(this.out);
      osc.start();
    }

    const noise = ctx.createBufferSource();
    noise.buffer = makeNoise(ctx, 4);
    noise.loop = true;
    const air = ctx.createBiquadFilter();
    air.type = "lowpass";
    air.frequency.value = 280;
    air.Q.value = 0.55;
    const airGain = ctx.createGain();
    airGain.gain.value = 0.05;
    noise.connect(air);
    air.connect(airGain);
    airGain.connect(this.out);

    const breath = ctx.createOscillator();
    breath.frequency.value = 0.16;
    const breathDepth = ctx.createGain();
    breathDepth.gain.value = 0.018;
    breath.connect(breathDepth);
    breathDepth.connect(airGain.gain);
    breath.start();
    noise.start();
  }

  set(amount: number, now: number): void {
    this.out.gain.setTargetAtTime(Math.max(0, amount) * 0.36, now, 0.95);
  }
}

function slotHz(from: Chord, to: Chord, t: number, slot: number, alt: number, pent: number[]): number {
  const a = voiced(from, slot, alt, pent);
  const b = voiced(to, slot, alt, pent);
  const u = clamp(t, 0, 1);
  return a + (b - a) * u;
}

function voiced(chord: Chord, slot: number, alt: number, pent: number[]): number {
  const roles = [chord.bass, chord.fifth, chord.mid, chord.high, chord.high2, chord.color];
  const hz = roles[slot] ?? chord.mid;
  const choice = alt % 3;
  if (choice === 1) return nearestPent(hz * 2, pent);
  if (choice === 2) return nearestPent(slot < 2 ? hz * 1.5 : hz * 0.5, pent);
  return nearestPent(hz, pent);
}

function nearestPent(hz: number, pent: number[]): number {
  let best = pent[0];
  let bestDist = Infinity;
  for (const n of pent) {
    const d = Math.abs(Math.log2(Math.max(20, hz) / n));
    if (d < bestDist) {
      bestDist = d;
      best = n;
    }
  }
  return best;
}

function uniqueSorted(freqs: number[]): number[] {
  const out: number[] = [];
  for (const hz of freqs) {
    if (out.every((n) => Math.abs(Math.log2(hz / n)) > 0.04)) out.push(hz);
  }
  out.sort((a, b) => a - b);
  return out;
}

function makeNoise(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let brown = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    brown = clamp(brown + white * 0.02, -1, 1);
    data[i] = white * 0.35 + brown * 0.65;
  }
  return buf;
}

function makeImpulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      const t = i / len;
      data[i] = (Math.random() * 2 - 1) * (1 - t) ** 3.1;
    }
  }
  return buf;
}
